
import React from 'react';
import type { ChatMessage, ToolResult, GA4ReportData, GTMContainerInfo, AdSuggestion, GTMTagSuggestion } from '../types';
import ReportChart from './ReportChart';
import { LogoIcon } from './icons';

declare global {
  interface Window {
    marked: {
      parse(markdown: string): string;
    };
  }
}

const renderToolResult = (result: ToolResult) => {
  switch (result.type) {
    case 'ga4_report':
      return <GA4ReportCard data={result.data} />;
    case 'gtm_info':
      return <GTMInfoCard data={result.data} />;
    case 'ad_suggestion':
      return <AdSuggestionCard data={result.data} />;
    case 'gtm_suggestion':
        return <GTMTagSuggestionCard data={result.data} />;
    default:
      return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>;
  }
};

const GA4ReportCard: React.FC<{data: GA4ReportData[]}> = ({ data }) => (
    <div className="bg-base-200 rounded-lg overflow-hidden my-2">
        <div className="p-4 border-b border-base-300">
            <h3 className="font-bold text-lg">GA4 Performance Report</h3>
            <p className="text-sm text-text-secondary">Here's the session and conversion data for the last 7 days.</p>
        </div>
        <div className="p-4">
            <ReportChart data={data} />
        </div>
    </div>
);

const GTMInfoCard: React.FC<{data: GTMContainerInfo}> = ({ data }) => (
    <div className="bg-base-200 rounded-lg p-4 my-2">
        <h3 className="font-bold text-lg">GTM Container Snapshot</h3>
        <p className="text-sm text-text-secondary mb-4">{data.name} ({data.containerId})</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
                <h4 className="font-semibold text-brand-secondary mb-2">Tags ({data.tags.length})</h4>
                <ul className="list-disc list-inside text-text-secondary">
                    {data.tags.map(t => <li key={t.name}>{t.name}</li>)}
                </ul>
            </div>
            <div>
                <h4 className="font-semibold text-brand-secondary mb-2">Triggers ({data.triggers.length})</h4>
                <ul className="list-disc list-inside text-text-secondary">
                    {data.triggers.map(t => <li key={t.name}>{t.name}</li>)}
                </ul>
            </div>
            <div>
                <h4 className="font-semibold text-brand-secondary mb-2">Variables ({data.variables.length})</h4>
                <ul className="list-disc list-inside text-text-secondary">
                    {data.variables.map(t => <li key={t.name}>{t.name}</li>)}
                </ul>
            </div>
        </div>
    </div>
);

const AdSuggestionCard: React.FC<{data: AdSuggestion}> = ({ data }) => (
    <div className="bg-base-200 rounded-lg p-4 my-2 border border-brand-primary/50">
        <h3 className="font-bold text-lg text-brand-secondary">New Ad Suggestion</h3>
        <p className="text-lg font-semibold mt-2">{data.headline}</p>
        <p className="text-text-secondary mt-1">{data.description}</p>
        <div className="mt-4">
            <h4 className="font-semibold text-sm">Keywords:</h4>
            <div className="flex flex-wrap gap-2 mt-2">
                {data.keywords.map(k => <span key={k} className="bg-base-300 text-xs font-medium px-2 py-1 rounded-full">{k}</span>)}
            </div>
        </div>
    </div>
);

const GTMTagSuggestionCard: React.FC<{data: GTMTagSuggestion}> = ({ data }) => (
    <div className="bg-base-200 rounded-lg p-4 my-2 border border-green-500/50">
        <h3 className="font-bold text-lg text-green-400">GTM Tag Suggestion</h3>
        <p className="text-text-secondary mb-4">{data.description}</p>
        <div className="font-mono text-xs bg-base-300 p-3 rounded-md text-text-secondary">
            <p><span className="text-text-primary">Tag Name:</span> {data.name}</p>
            <p><span className="text-text-primary">Tag Type:</span> {data.type}</p>
            <p><span className="text-text-primary">Event Name:</span> {data.event}</p>
            <p className="mt-2"><span className="text-text-primary">Trigger Name:</span> {data.trigger.name}</p>
            <p><span className="text-text-primary">Trigger Type:</span> {data.trigger.type}</p>
            <p><span className="text-text-primary">Trigger Condition:</span> {data.trigger.condition}</p>
        </div>
    </div>
);

const ChatMessageComponent: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isModel = message.role === 'model';

  const renderContent = () => {
    if (typeof message.content === 'string') {
        const html = window.marked.parse(message.content);
        return <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
    }
    return renderToolResult(message.content);
  };

  return (
    <div className={`flex items-start gap-4 my-4 ${isModel ? '' : 'flex-row-reverse'}`}>
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isModel ? 'bg-brand-primary' : 'bg-base-300'}`}>
        {isModel ? <LogoIcon className="w-6 h-6 text-white" /> : <span className="text-lg font-bold">U</span>}
      </div>
      <div className={`max-w-2xl p-4 rounded-lg ${isModel ? 'bg-base-200' : 'bg-brand-primary'}`}>
        {renderContent()}
      </div>
    </div>
  );
};

export default ChatMessageComponent;
